import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { formatApiError } from '@/lib/api';

const AuthContext = createContext(null);

async function fetchProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

export function AuthProvider({ children }) {
  // null = still loading, false = unauthenticated, object = profile+session
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const hydrateUser = useCallback(async (supabaseSession) => {
    if (!supabaseSession) {
      setSession(null);
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const profile = await fetchProfile(supabaseSession.user.id);
      setSession(supabaseSession);
      setUser({ ...profile, auth: supabaseSession.user });
    } catch {
      setSession(supabaseSession);
      // Profile trigger may not have fired yet — surface auth user at minimum
      setUser({ auth: supabaseSession.user });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Hydrate from existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      hydrateUser(session);
    });

    // Keep in sync with Supabase auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      hydrateUser(session);
    });

    return () => subscription.unsubscribe();
  }, [hydrateUser]);

  const login = async (email, password) => {
    setError('');
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      setError(formatApiError(err));
      return false;
    }
    await hydrateUser(data.session);
    return true;
  };

  // `extra` rides along as user metadata: company, signup_source, lead_id.
  // handle_new_user (migration 031) copies company and source to the profile.
  const register = async (name, email, password, extra = {}) => {
    setError('');
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, ...extra } },
    });
    if (err) {
      setError(formatApiError(err));
      return { ok: false };
    }
    // When "Confirm email" is on, signUp returns no session — a 6-digit
    // code has been emailed and must be verified before login.
    if (!data.session) {
      return { ok: true, needsVerification: true };
    }
    await hydrateUser(data.session);
    return { ok: true, needsVerification: false };
  };

  const verifyEmailOtp = async (email, token) => {
    setError('');
    const { data, error: err } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });
    if (err) {
      setError(formatApiError(err));
      return false;
    }
    await hydrateUser(data.session);
    return true;
  };

  const resendSignupOtp = async (email) => {
    setError('');
    const { error: err } = await supabase.auth.resend({ type: 'signup', email });
    if (err) {
      setError(formatApiError(err));
      return false;
    }
    return true;
  };

  const loginWithGoogle = async () => {
    setError('');
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/research`,
      },
    });
    if (err) {
      setError(formatApiError(err));
      return false;
    }
    // Browser redirects to Google; session hydrates via onAuthStateChange on return.
    return true;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(false);
    setSession(null);
  };

  const refreshUser = async () => {
    if (!session) return;
    try {
      const profile = await fetchProfile(session.user.id);
      setUser({ ...profile, auth: session.user });
    } catch { /* noop */ }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, error, login, loginWithGoogle, register, verifyEmailOtp, resendSignupOtp, logout, refreshUser, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
